import { Label } from "@/components/ui/label";
import { AlertCircle } from "lucide-react";
import { useI18n } from "@/hooks/useI18n";

interface FormFieldProps {
  /** Id dell'input figlio: serve a legare la label al campo. */
  id?: string;
  label: string;
  icon?: React.ReactNode;
  /** Obbligatorio marca con `*`, altrimenti compare "· opzionale". */
  required?: boolean;
  /** Sopprime del tutto il marcatore, per campi dove non aggiunge nulla. */
  hideOptional?: boolean;
  error?: string;
  /** Testo di aiuto sotto il campo: nascosto quando c'e' un errore. */
  hint?: string;
  children: React.ReactNode;
}

/**
 * Campo di form standard del prodotto: label piccola con icona opzionale,
 * marcatore obbligatorio/opzionale, hint e messaggio di errore.
 *
 * Usato dalle modali (richieste cliente, attivita' del builder) per avere le
 * stesse dimensioni di testo e le stesse spaziature ovunque. Non impagina i
 * campi: il layout resta al chiamante (griglie, sezioni).
 */
export const FormField = ({
  id, label, icon, required, hideOptional, error, hint, children,
}: FormFieldProps) => {
  const { t } = useI18n();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs flex items-center gap-1 text-gray-700">
        {icon}
        {label}
        {required && <span className="text-red-500">*</span>}
        {!required && !hideOptional && (
          <span className="text-gray-400 font-normal">· {t('common.optional')}</span>
        )}
      </Label>
      {children}
      {hint && !error && <p className="text-[11px] text-gray-500">{hint}</p>}
      {error && (
        <p className="text-[11px] text-red-600 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {error}
        </p>
      )}
    </div>
  );
};

export default FormField;
